import { useEffect, useId, useMemo, useState, type KeyboardEvent } from "react";
import { Check, ChevronDown, Copy, FileText, Search, X } from "lucide-react";
import { useTranslation } from "@/common/providers/i18n-provider";
import { copyTextToClipboard } from "@/lib/clipboard";
import {
  DEFAULT_QUOTE,
  FIAT_CODES,
  MAX_PRICE_SOURCES,
  PRICE_OPTIONS_URL,
  QUOTE_PREFERENCE,
  parsePriceCatalog,
  preferredPriceQuote,
  quickPriceAssets,
  searchPriceAssets,
  selectedPriceIncludes,
  type PriceAsset,
  type PriceCatalog,
} from "@/lib/price-catalog";
import {
  extractOperatingCurrencies,
  inferPricePairs,
  SAMPLE_LEDGER,
} from "@/lib/price-pairs";

type CatalogState =
  | { status: "loading" | "error" }
  | { status: "ready"; catalog: PriceCatalog };
type ImportReview = {
  count: number;
  limited: boolean;
  issues: { code: string; reason: "unknown" | "delisted" | "ambiguous" }[];
};

function saveQuote(quote: string) {
  try {
    localStorage.setItem(QUOTE_PREFERENCE, quote);
  } catch {
    /* Storage is optional. */
  }
}

export function PricePicker() {
  const { t, locale } = useTranslation();
  const id = useId();
  const [load, setLoad] = useState<CatalogState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [quote, setQuote] = useState(DEFAULT_QUOTE);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [copyResult, setCopyResult] = useState<{
    text: string;
    ok: boolean;
  } | null>(null);
  const [ledger, setLedger] = useState("");
  const [review, setReview] = useState<ImportReview | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    let mounted = true;
    void fetch(PRICE_OPTIONS_URL, {
      credentials: "omit",
      signal: controller.signal,
      redirect: "error",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Price catalog unavailable");
        return parsePriceCatalog(await response.json());
      })
      .then((catalog) => {
        if (!mounted) return;
        let saved: string | null = null;
        try {
          saved = localStorage.getItem(QUOTE_PREFERENCE);
        } catch {
          /* Storage is optional. */
        }
        setQuote(
          preferredPriceQuote(
            catalog.quotes,
            locale,
            navigator.languages,
            saved,
          ),
        );
        setLoad({ status: "ready", catalog });
      })
      .catch(() => {
        if (mounted) setLoad({ status: "error" });
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      mounted = false;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [attempt, locale]);

  const catalog = load.status === "ready" ? load.catalog : null;
  const currencyNames = useMemo(
    () => new Intl.DisplayNames(locale, { type: "currency" }),
    [locale],
  );
  const number = new Intl.NumberFormat(locale);
  const count = (value: number) => number.format(value);
  const quoteLabel = (code: string) => {
    const name = /^[A-Z]{3}$/.test(code) ? currencyNames.of(code) : code;
    return name && name !== code ? `${code} · ${name}` : code;
  };
  const quick = useMemo(
    () => (catalog ? quickPriceAssets(catalog, quote) : []),
    [catalog, quote],
  );
  const matches = useMemo(
    () => (catalog ? searchPriceAssets(catalog, quote, query) : []),
    [catalog, quote, query],
  );
  const selected = selectedIds.flatMap((assetId) => {
    const asset = catalog?.byId.get(assetId);
    return asset ? [asset] : [];
  });
  const validCount = selected.filter((asset) => asset.quotes.has(quote)).length;
  const includes = selectedPriceIncludes(selected, quote);
  const copied = copyResult?.ok && copyResult.text === includes;
  const copyFailed = copyResult?.ok === false && copyResult.text === includes;
  const atLimit = selectedIds.length >= MAX_PRICE_SOURCES;
  const activeIndex = Math.min(active, matches.length - 1);
  const expanded = open && catalog !== null;

  useEffect(() => {
    if (expanded)
      document
        .getElementById(`${id}-result-${activeIndex}`)
        ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, expanded, id, query]);

  function changeQuote(value: string) {
    setQuote(value);
    saveQuote(value);
    setQuery("");
    setOpen(false);
    setActive(0);
    setReview(null);
  }

  function toggleAsset(asset: PriceAsset) {
    setSelectedIds((previous) =>
      previous.includes(asset.id)
        ? previous.filter((assetId) => assetId !== asset.id)
        : previous.length < MAX_PRICE_SOURCES
          ? [...previous, asset.id]
          : previous,
    );
    setReview(null);
  }

  function chooseResult(asset: PriceAsset) {
    if (atLimit && !selectedIds.includes(asset.id)) return;
    if (!selectedIds.includes(asset.id)) toggleAsset(asset);
    setQuery("");
    setOpen(false);
    setActive(0);
  }

  function searchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActive(
        !expanded
          ? 0
          : Math.max(
              0,
              Math.min(
                matches.length - 1,
                activeIndex + (event.key === "ArrowDown" ? 1 : -1),
              ),
            ),
      );
    } else if (event.key === "Enter" && expanded) {
      event.preventDefault();
      if (matches[activeIndex]) chooseResult(matches[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  function useLedger() {
    if (!catalog) return;
    const nextQuote =
      extractOperatingCurrencies(ledger).find((code) =>
        catalog.quotes.includes(code),
      ) ?? quote;
    const inferred = inferPricePairs(ledger, catalog, nextQuote);
    const identities = new Map<string, number>();
    for (const asset of catalog.assets)
      identities.set(asset.code, (identities.get(asset.code) ?? 0) + 1);
    const ids: string[] = [];
    const issues: ImportReview["issues"] = [];
    for (const pair of inferred.pairs) {
      if (pair.status !== "available")
        issues.push({ code: pair.base, reason: pair.status });
      else if ((identities.get(pair.base) ?? 0) > 1)
        issues.push({ code: pair.base, reason: "ambiguous" });
      else if (pair.assetId) ids.push(pair.assetId);
    }
    if (ids.length > 0) {
      changeQuote(nextQuote);
      setSelectedIds(ids.slice(0, MAX_PRICE_SOURCES));
    }
    setReview({
      count: Math.min(ids.length, MAX_PRICE_SOURCES),
      limited: ids.length > MAX_PRICE_SOURCES,
      issues,
    });
  }

  async function copyIncludes() {
    if (!includes) return;
    const text = includes;
    setCopyResult({ text, ok: await copyTextToClipboard(text) });
  }

  return (
    <section
      id="price-picker"
      className="lp-example lp-picker"
      aria-labelledby={`${id}-title`}
    >
      <div className="lp-picker-body" aria-busy={load.status === "loading"}>
        <div className="lp-picker-heading">
          <FileText size={19} aria-hidden="true" />
          <h2 id={`${id}-title`}>{t("livePrices.picker.title")}</h2>
        </div>
        <label className="lp-field-label" htmlFor={`${id}-quote`}>
          {t("livePrices.picker.currency")}
        </label>
        <div className="lp-quote-field">
          <select
            id={`${id}-quote`}
            value={quote}
            disabled={!catalog}
            onChange={(event) => changeQuote(event.target.value)}
          >
            {catalog ? (
              <>
                <optgroup label={t("livePrices.picker.fiat")}>
                  {catalog.quotes
                    .filter((code) => FIAT_CODES.has(code))
                    .map((code) => (
                      <option key={code} value={code}>
                        {quoteLabel(code)}
                      </option>
                    ))}
                </optgroup>
                <optgroup label={t("livePrices.picker.otherQuotes")}>
                  {catalog.quotes
                    .filter((code) => !FIAT_CODES.has(code))
                    .map((code) => (
                      <option key={code} value={code}>
                        {quoteLabel(code)}
                      </option>
                    ))}
                </optgroup>
              </>
            ) : (
              <option value={quote}>{quoteLabel(quote)}</option>
            )}
          </select>
          <ChevronDown size={17} aria-hidden="true" />
        </div>

        <fieldset className="lp-assets-field" disabled={!catalog}>
          <legend className="lp-field-label">
            {t("livePrices.picker.assets")}
          </legend>
          {load.status === "loading" ? (
            <p className="lp-quick-placeholder" role="status">
              {t("livePrices.picker.loading")}
            </p>
          ) : null}
          {quick.length > 0 ? (
            <>
              <p className="lp-picker-caption">
                {t("livePrices.picker.quick")}
              </p>
              <div className="lp-quick-picks">
                {quick.map((asset) => {
                  const chosen = selectedIds.includes(asset.id);
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      aria-pressed={chosen}
                      disabled={atLimit && !chosen}
                      onClick={() => toggleAsset(asset)}
                    >
                      {chosen ? (
                        <Check size={13} aria-hidden="true" />
                      ) : (
                        <span className="lp-pill-dot" aria-hidden="true" />
                      )}
                      <bdi>{asset.code}</bdi>
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}
          <div
            className="lp-asset-search"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                setOpen(false);
            }}
          >
            <label className="sr-only" htmlFor={`${id}-search`}>
              {t("livePrices.picker.searchLabel")}
            </label>
            <div className="lp-search-field">
              <Search size={17} aria-hidden="true" />
              <input
                id={`${id}-search`}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={expanded}
                aria-controls={`${id}-results`}
                aria-activedescendant={
                  expanded && matches[activeIndex]
                    ? `${id}-result-${activeIndex}`
                    : undefined
                }
                autoComplete="off"
                spellCheck={false}
                value={query}
                placeholder={t("livePrices.picker.searchPlaceholder")}
                onFocus={() => setOpen(true)}
                onKeyDown={searchKeyDown}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                  setOpen(true);
                }}
              />
            </div>
            {expanded ? (
              <div className="lp-search-popup">
                <div
                  id={`${id}-results`}
                  role="listbox"
                  aria-label={t("livePrices.picker.searchLabel")}
                >
                  {matches.map((asset, index) => {
                    const chosen = selectedIds.includes(asset.id);
                    return (
                      <button
                        key={asset.id}
                        id={`${id}-result-${index}`}
                        role="option"
                        aria-selected={index === activeIndex}
                        aria-disabled={atLimit && !chosen}
                        type="button"
                        tabIndex={-1}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseMove={() => setActive(index)}
                        onClick={() => chooseResult(asset)}
                      >
                        <bdi>{asset.code}</bdi>
                        <span>{t(`livePrices.picker.kind.${asset.kind}`)}</span>
                        {chosen ? (
                          <Check
                            size={15}
                            aria-label={t("livePrices.picker.selected")}
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                {matches.length === 0 ? (
                  <p role="status">
                    {t("livePrices.picker.noResults", { quote })}
                  </p>
                ) : null}
                <p className="lp-search-footnote">
                  {t("livePrices.picker.searchHint")}
                </p>
              </div>
            ) : null}
          </div>
        </fieldset>

        {load.status === "error" ? (
          <div className="lp-catalog-state" role="alert">
            <p>{t("livePrices.picker.loadError")}</p>
            <button
              type="button"
              className="lp-text-link"
              onClick={() => {
                setLoad({ status: "loading" });
                setAttempt((value) => value + 1);
              }}
            >
              {t("livePrices.picker.retry")}
            </button>
          </div>
        ) : null}

        {selected.length > 0 ? (
          <div className="lp-selection">
            <p className="lp-picker-caption">
              {t("livePrices.picker.selection", {
                count: count(selected.length),
              })}
            </p>
            <ul className="lp-selected-assets">
              {selected.map((asset) => (
                <li key={asset.id} data-available={asset.quotes.has(quote)}>
                  <span>
                    <bdi>{asset.code}</bdi>
                    {!asset.quotes.has(quote) ? (
                      <small>
                        {t("livePrices.picker.notListed", { quote })}
                      </small>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    aria-label={t("livePrices.picker.remove", {
                      code: asset.code,
                    })}
                    onClick={() => toggleAsset(asset)}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {atLimit ? (
          <p className="lp-picker-note" role="status">
            {t("livePrices.picker.limit", { count: count(MAX_PRICE_SOURCES) })}
          </p>
        ) : null}
        {validCount < selected.length ? (
          <p className="lp-picker-note" role="status">
            {t("livePrices.picker.unavailable")}
          </p>
        ) : null}

        <details className="lp-ledger-import">
          <summary>{t("livePrices.picker.import")}</summary>
          <label className="sr-only" htmlFor={`${id}-ledger`}>
            {t("livePrices.picker.ledgerLabel")}
          </label>
          <textarea
            id={`${id}-ledger`}
            dir="ltr"
            rows={4}
            maxLength={100000}
            spellCheck={false}
            value={ledger}
            placeholder={t("livePrices.picker.ledgerPlaceholder")}
            onChange={(event) => {
              setLedger(event.target.value);
              setReview(null);
            }}
          />
          <div className="lp-import-actions">
            <button
              className="lp-refresh"
              type="button"
              disabled={!catalog || !ledger.trim()}
              onClick={useLedger}
            >
              {t("livePrices.picker.useLedger")}
            </button>
            <button
              className="lp-text-link"
              type="button"
              onClick={() => {
                setLedger(SAMPLE_LEDGER);
                setReview(null);
              }}
            >
              {t("livePrices.picker.sample")}
            </button>
          </div>
          <p className="lp-picker-note">{t("livePrices.picker.privacy")}</p>
          {review ? (
            <div className="lp-import-review" role="status">
              <p>
                {t("livePrices.picker.imported", {
                  count: count(review.count),
                })}
              </p>
              {review.limited ? (
                <p>
                  {t("livePrices.picker.importLimit", {
                    count: count(MAX_PRICE_SOURCES),
                  })}
                </p>
              ) : null}
              {review.issues.length > 0 ? (
                <ul>
                  {review.issues.map((issue) => (
                    <li key={issue.code}>
                      <bdi>{issue.code}</bdi> —{" "}
                      {t(`livePrices.picker.${issue.reason}`)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </details>
      </div>

      <div className="lp-source lp-picker-output">
        <div className="lp-source-top">
          <span>{t("livePrices.picker.output")}</span>
          <button
            className="lp-copy"
            type="button"
            disabled={!includes}
            onClick={copyIncludes}
          >
            {copied ? (
              <Check size={15} aria-hidden="true" />
            ) : (
              <Copy size={15} aria-hidden="true" />
            )}
            {copied
              ? t("livePrices.copied")
              : t("livePrices.picker.copy", { count: count(validCount) })}
          </button>
        </div>
        <pre className="lp-includes" dir="ltr">
          <code>{includes || `; ${t("livePrices.picker.empty")}`}</code>
        </pre>
        <p role="status" className={copyFailed ? "lp-copy-error" : "sr-only"}>
          {copyFailed
            ? t("livePrices.copyError")
            : copied
              ? t("livePrices.copied")
              : ""}
        </p>
        <p className="lp-output-hint">{t("livePrices.picker.paste")}</p>
      </div>
    </section>
  );
}
